/**
 * Code Signing & AV Verification — admin console port.
 *
 * Source: /Users/robert/dashboard/src/views/admin/CodeSigning/index.jsx
 * Endpoint: GET /admin-services/code-signing (not yet wired in nurev-api;
 *   screen surfaces isError state until it ships).
 *
 * What this surfaces:
 *   - Code-signing certificate status (issuer, subject, expiry countdown,
 *     SHA-256 fingerprint)
 *   - VirusTotal verdict for the latest signed binary
 *   - Per-vendor verdict grid (Microsoft Defender, Norton, McAfee, etc.)
 *   - Latest signed build metadata + MSI download link
 *   - Quick-action tiles (VirusTotal scan trigger, 24h Defender harness,
 *     direct VirusTotal link)
 *
 * Auto-refreshes every 5 minutes via react-query.
 *
 * Envelope: client.get() → { data: { success: true, data: <payload> } }
 * unwrap() peels r.data.data, yielding CodeSigningData directly.
 */
import { ExternalLink, RefreshCw, Shield, CheckCircle, AlertTriangle, Play, Clock } from 'lucide-react';
import { useCodeSigning } from '@/hooks/useCodeSigning';
import { VendorVerdict } from '@/api/codeSigning';
import {
  Card,
  SectionLabel,
  Skeleton,
  DetailCell
} from '@/components/ui/atoms';
import { fmtDateTime, fmtDate } from '@/lib/format';

// ─── Cert level helpers ───────────────────────────────────────────────────

type CertLevel = 'healthy' | 'warn' | 'error' | 'expired' | 'unknown';

function certLevel(validTo?: string): CertLevel {
  if (!validTo) return 'unknown';
  const days = Math.floor(
    (new Date(validTo).getTime() - Date.now()) / (24 * 3_600_000)
  );
  if (days < 0) return 'expired';
  if (days < 7) return 'error';
  if (days < 30) return 'warn';
  return 'healthy';
}

function certDaysLeft(validTo?: string): number | null {
  if (!validTo) return null;
  return Math.floor(
    (new Date(validTo).getTime() - Date.now()) / (24 * 3_600_000)
  );
}

// ─── Cert hero ────────────────────────────────────────────────────────────

const LEVEL_HERO: Record<CertLevel, string> = {
  healthy: 'from-emerald-500/8 via-nurev-surface to-nurev-bg border-emerald-500/30',
  warn: 'from-amber-500/10 via-nurev-surface to-nurev-bg border-amber-500/40',
  error: 'from-red-500/10 via-nurev-surface to-nurev-bg border-red-500/40',
  expired: 'from-red-500/10 via-nurev-surface to-nurev-bg border-red-500/40',
  unknown: 'from-white/[0.02] via-nurev-surface to-nurev-bg border-nurev-border'
};

const LEVEL_ICON: Record<CertLevel, string> = {
  healthy: 'text-emerald-400',
  warn: 'text-amber-300',
  error: 'text-red-400',
  expired: 'text-red-400',
  unknown: 'text-nurev-muted'
};

const LEVEL_DAYS_COLOR: Record<CertLevel, string> = {
  healthy: 'text-emerald-300',
  warn: 'text-amber-300',
  error: 'text-red-400',
  expired: 'text-red-400',
  unknown: 'text-nurev-text'
};

const LEVEL_BADGE: Record<CertLevel, string> = {
  healthy: 'bg-emerald-500/20 text-emerald-300 ring-1 ring-emerald-500/30',
  warn: 'bg-amber-500/20 text-amber-300 ring-1 ring-amber-500/30',
  error: 'bg-red-500/20 text-red-300 ring-1 ring-red-500/30',
  expired: 'bg-red-500/20 text-red-300 ring-1 ring-red-500/30',
  unknown: 'bg-white/10 text-nurev-muted ring-1 ring-white/10'
};

function CertLevelBadge({ level }: { level: CertLevel }) {
  const label: Record<CertLevel, string> = {
    healthy: 'Valid',
    warn: 'Expiring soon',
    error: 'Expires < 7 days',
    expired: 'EXPIRED',
    unknown: 'Unknown'
  };
  return (
    <span
      className={`inline-block rounded px-2 py-0.5 text-[10px] font-medium ${LEVEL_BADGE[level]}`}
    >
      {label[level]}
    </span>
  );
}

// ─── Vendor verdict cell ──────────────────────────────────────────────────

function VendorCell({ v }: { v: VendorVerdict }) {
  const ok =
    v.verdict === 'clean' ||
    v.verdict === 'undetected' ||
    v.verdict === 'pass';
  return (
    <div
      className={`flex items-center gap-2 rounded-lg px-3 py-2 ${
        ok ? 'bg-emerald-500/[0.08]' : 'bg-red-500/10'
      }`}
      title={v.escalation || v.verdict || ''}
    >
      {ok ? (
        <CheckCircle className="h-3 w-3 flex-shrink-0 text-emerald-400" />
      ) : (
        <AlertTriangle className="h-3 w-3 flex-shrink-0 text-red-400" />
      )}
      <span
        className={`truncate text-[11px] font-bold ${
          ok ? 'text-emerald-300' : 'text-red-400'
        }`}
      >
        {v.name}
      </span>
    </div>
  );
}

// ─── Screen ───────────────────────────────────────────────────────────────

export function CodeSigningScreen() {
  const { data, isLoading, isFetching, isError, refetch } = useCodeSigning();

  const cert = data?.cert;
  const build = data?.latestBuild;
  const vt = data?.virusTotal;
  const vendors = data?.vendors ?? [];

  const level = certLevel(cert?.validTo);
  const days = certDaysLeft(cert?.validTo);

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      {/* ── Page header ── */}
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-nurev-text lg:text-2xl">
            Code Signing &amp; AV Verification
          </h1>
          <p className="mt-1 text-xs text-nurev-muted">
            Cert health, VirusTotal verdicts, per-vendor status. Auto-refreshes
            every 5 min.
          </p>
        </div>
        <button
          onClick={() => refetch()}
          disabled={isFetching}
          className="inline-flex items-center gap-1.5 rounded-lg border border-nurev-border bg-white/[0.04] px-3 py-1.5 text-xs font-bold text-nurev-text transition-colors hover:bg-white/[0.07] disabled:opacity-50"
        >
          <RefreshCw
            className={`h-3 w-3 ${isFetching ? 'animate-spin' : ''}`}
          />
          Refresh
        </button>
      </header>

      {/* ── API error banner ── */}
      {isError && (
        <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 p-4 text-sm text-amber-300">
          Could not load code-signing data. The{' '}
          <code className="font-mono text-[11px]">
            /admin-services/code-signing
          </code>{' '}
          endpoint may not be wired yet — check nurev-api
          app/controllers/admin-services.js.
        </div>
      )}

      {/* ── Cert status hero ── */}
      <div
        className={`relative overflow-hidden rounded-2xl border bg-gradient-to-br p-5 ${
          isLoading
            ? 'border-nurev-border from-white/[0.02] via-nurev-surface to-nurev-bg'
            : LEVEL_HERO[level]
        }`}
      >
        {isLoading ? (
          <div className="space-y-3">
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-8 w-3/4" />
          </div>
        ) : (
          <>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="flex items-start gap-3">
                <div className="flex h-12 w-12 items-center justify-center rounded-xl border border-nurev-border bg-white/[0.05]">
                  <Shield
                    className={`h-5 w-5 ${
                      cert ? LEVEL_ICON[level] : 'text-nurev-muted'
                    }`}
                  />
                </div>
                <div>
                  <p className="text-sm font-bold text-nurev-text">
                    Code-signing certificate
                  </p>
                  {cert ? (
                    <>
                      <p className="mt-0.5 text-[11px] text-nurev-muted">
                        {cert.issuer || 'Unknown issuer'}
                      </p>
                      <div className="mt-1.5">
                        <CertLevelBadge level={level} />
                      </div>
                    </>
                  ) : (
                    <p className="mt-0.5 text-[11px] text-nurev-muted">
                      Cert renewal in flight (Sectigo EV) — expected ~10d
                    </p>
                  )}
                </div>
              </div>

              {/* Days remaining counter */}
              {days !== null && (
                <div className="text-right">
                  <p className="mb-1 text-[10px] font-bold uppercase tracking-wider text-nurev-muted">
                    Days remaining
                  </p>
                  <p
                    className={`text-3xl font-extrabold ${
                      LEVEL_DAYS_COLOR[level]
                    }`}
                  >
                    {Math.max(days, 0).toLocaleString()}
                  </p>
                  {days < 0 && (
                    <p className="mt-0.5 text-[11px] font-bold text-red-400">
                      EXPIRED
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Cert detail cells */}
            {cert && (
              <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
                <div className="rounded-lg bg-white/[0.04] px-3 py-2">
                  <DetailCell label="Subject">
                    <span className="truncate">{cert.subject || '—'}</span>
                  </DetailCell>
                </div>
                <div className="rounded-lg bg-white/[0.04] px-3 py-2">
                  <DetailCell label="Valid through">
                    {cert.validTo ? fmtDate(cert.validTo) : '—'}
                  </DetailCell>
                </div>
                <div className="rounded-lg bg-white/[0.04] px-3 py-2">
                  <DetailCell label="SHA-256" mono>
                    <span
                      className="truncate text-[10px]"
                      title={cert.fingerprint}
                    >
                      {cert.fingerprint || '—'}
                    </span>
                  </DetailCell>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* ── Latest build + VirusTotal ── */}
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-3">
        {/* Latest signed build */}
        <Card className="lg:col-span-2">
          <SectionLabel>Latest signed build</SectionLabel>
          {isLoading ? (
            <Skeleton className="h-24 w-full" />
          ) : build ? (
            <div className="grid grid-cols-2 gap-4 text-xs">
              <DetailCell label="Version">
                <span className="font-bold">{build.version || '—'}</span>
              </DetailCell>
              <DetailCell label="Signed at">
                {build.signedAt ? fmtDateTime(build.signedAt) : '—'}
              </DetailCell>
              <div className="col-span-2">
                <DetailCell label="SHA-256" mono>
                  <span className="break-all text-[10.5px]">
                    {build.sha256 || '—'}
                  </span>
                </DetailCell>
              </div>
              {build.msi && (
                <div className="col-span-2">
                  <a
                    href={build.msi}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-[11px] text-amber-300 hover:underline"
                  >
                    Download MSI
                    <ExternalLink className="h-2.5 w-2.5" />
                  </a>
                </div>
              )}
            </div>
          ) : (
            <p className="text-xs text-nurev-muted">
              No signed build available yet — this populates after the renewed
              cert is used to sign installer.msi.
            </p>
          )}
        </Card>

        {/* VirusTotal verdict */}
        <Card>
          <SectionLabel>
            <span className="flex items-center gap-2">
              VirusTotal
              <a
                href={vt?.link ?? 'https://www.virustotal.com'}
                target="_blank"
                rel="noopener noreferrer"
                className="text-nurev-muted hover:text-amber-300"
              >
                <ExternalLink className="h-3 w-3" />
              </a>
            </span>
          </SectionLabel>
          {isLoading ? (
            <Skeleton className="h-20 w-full" />
          ) : vt ? (
            <div className="py-2 text-center">
              <p
                className={`text-4xl font-extrabold ${
                  vt.detections === 0
                    ? 'text-emerald-300'
                    : (vt.detections ?? 0) <= 2
                      ? 'text-amber-300'
                      : 'text-red-400'
                }`}
              >
                {vt.detections}
                <span className="text-2xl text-nurev-muted">
                  {' '}
                  / {vt.total}
                </span>
              </p>
              <p className="mt-1 text-[11px] text-nurev-muted">detections</p>
              <p className="mt-2 text-[10px] text-nurev-muted">
                Last scan:{' '}
                {vt.lastScanAt ? fmtDateTime(vt.lastScanAt) : '—'}
              </p>
              {vt.detections === 0 && (
                <div className="mt-3">
                  <span className="inline-block rounded bg-emerald-500/20 px-2 py-0.5 text-[10px] font-medium text-emerald-300 ring-1 ring-emerald-500/30">
                    Clean across all vendors
                  </span>
                </div>
              )}
            </div>
          ) : (
            <p className="py-3 text-center text-xs text-nurev-muted">
              No scan available yet — runs automatically after a new signed
              build is uploaded.
            </p>
          )}
        </Card>
      </div>

      {/* ── Per-vendor verdict grid ── */}
      <Card className="overflow-hidden !p-0">
        <div className="flex items-center justify-between border-b border-nurev-border px-5 py-3">
          <p className="text-sm font-bold text-nurev-text">
            Per-vendor verdict
          </p>
          <span className="text-[10px] text-nurev-muted">Top 15 AV vendors</span>
        </div>
        {isLoading ? (
          <div className="p-5">
            <Skeleton className="h-24 w-full" />
          </div>
        ) : vendors.length > 0 ? (
          <div className="grid grid-cols-2 gap-1 p-2 sm:grid-cols-3 lg:grid-cols-5">
            {vendors.map(v => (
              <VendorCell key={v.name} v={v} />
            ))}
          </div>
        ) : (
          <p className="px-5 py-8 text-center text-xs text-nurev-muted">
            Per-vendor verdicts populate after a VirusTotal scan completes on
            the new signed build. Run the 24-hour Defender harness at{' '}
            <code className="font-mono text-[11px] text-amber-300">
              /Users/robert/nurev-av-test/
            </code>{' '}
            to capture the Microsoft verdict separately.
          </p>
        )}
      </Card>

      {/* ── Quick actions ── */}
      <Card>
        <SectionLabel>Quick actions</SectionLabel>
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
          {/* Trigger VirusTotal scan — coming soon */}
          <button
            disabled
            className="cursor-not-allowed rounded-lg border border-nurev-border bg-white/[0.02] px-3 py-2.5 text-left"
          >
            <div className="flex items-center gap-2 text-xs font-bold text-nurev-muted">
              <Play className="h-3 w-3" />
              Trigger VirusTotal scan
            </div>
            <p className="mt-0.5 text-[10px] font-medium text-nurev-muted">
              Coming soon — automated
            </p>
          </button>

          {/* Run 24h Defender harness — coming soon */}
          <button
            disabled
            className="cursor-not-allowed rounded-lg border border-nurev-border bg-white/[0.02] px-3 py-2.5 text-left"
          >
            <div className="flex items-center gap-2 text-xs font-bold text-nurev-muted">
              <Clock className="h-3 w-3" />
              Run 24h Defender harness
            </div>
            <p className="mt-0.5 text-[10px] font-medium text-nurev-muted">
              Microsoft Defender, full-system scan
            </p>
          </button>

          {/* Open VirusTotal directly */}
          <a
            href="https://www.virustotal.com"
            target="_blank"
            rel="noopener noreferrer"
            className="rounded-lg border border-nurev-border bg-white/[0.04] px-3 py-2.5 transition-colors hover:bg-white/[0.07]"
          >
            <div className="flex items-center gap-2 text-xs font-bold text-nurev-text">
              <ExternalLink className="h-3 w-3" />
              Open VirusTotal
            </div>
            <p className="mt-0.5 text-[10px] font-medium text-nurev-muted">
              Manually upload MSI for ad-hoc scan
            </p>
          </a>
        </div>
      </Card>
    </div>
  );
}
